// Central API layer: one base URL, one fetch wrapper for the whole app.
//
// Fallback matches the backend's local port (chat.py serves :5002).
// Production builds set REACT_APP_BACKEND_URL (see .env.production).
//
// Identity: the backend no longer trusts any client-supplied user_id.
// Signed-in requests carry "Authorization: Bearer <Clerk session token>",
// which the backend verifies; signed-out visitors are identified by a
// server-signed session cookie (credentials: 'include').
export const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5002';

export const getAuthToken = async () => {
  try {
    const session = typeof window !== 'undefined' ? window.Clerk?.session : null;
    if (session) return await session.getToken();
  } catch (_) {
    // signed out or Clerk not ready yet: anonymous request
  }
  return null;
};

export const authHeaders = async (headers = {}) => {
  const merged = { ...(headers || {}) };
  const hasAuth = Object.keys(merged).some((k) => k.toLowerCase() === 'authorization');
  if (!hasAuth) {
    const token = await getAuthToken();
    if (token) merged.Authorization = `Bearer ${token}`;
  }
  return merged;
};

export const fetchWithAuth = async (url, options = {}) => {
  const headers = await authHeaders(options.headers);
  return fetch(url, { credentials: 'include', ...options, headers });
};

export const apiFetch = (path, options = {}) =>
  fetchWithAuth(`${API_BASE_URL}${path}`, options);

const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getJson = async (
  path,
  {
    timeoutMs = 15000,
    retries,
    retryDelayMs = 600,
    headers,
    ...options
  } = {}
) => {
  const method = (options.method || 'GET').toUpperCase();
  const attempts = retries !== undefined ? retries : method === 'GET' ? 1 : 0;
  let lastError;

  for (let attempt = 0; attempt <= attempts; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await apiFetch(path, {
        ...options,
        headers: { Accept: 'application/json', ...(headers || {}) },
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = new Error(`Request failed (${res.status})`);
        err.status = res.status;
        try {
          err.data = await res.json();
        } catch (_) {
          err.data = null;
        }
        throw err;
      }
      return await res.json();
    } catch (err) {
      lastError = err;
      const transient =
        err.name === 'AbortError' ||
        err.name === 'TypeError' ||
        RETRYABLE_STATUSES.has(err.status);
      if (!transient || attempt === attempts) throw err;
      await sleep(retryDelayMs * (2 ** attempt) + Math.random() * 250);
    } finally {
      clearTimeout(timer);
    }
  }
  throw lastError;
};

export const fetchUsage = () => getJson('/api/usage');

export const fetchPlans = () => getJson('/api/plans');

export const fetchEngine = () =>
  getJson('/api/engine').catch(() => null);

export const fetchConversations = (page = 1, limit = 20) =>
  getJson(`/api/conversations?page=${page}&limit=${limit}`);

export const createConversation = (title) =>
  postJson('/api/conversations', title ? { title } : {});

export const fetchConversationDetail = (conversationId) =>
  getJson(`/api/conversations/${encodeURIComponent(conversationId)}`);

export const updateConversation = (conversationId, patch) =>
  getJson(`/api/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });

const postJson = (path, body) =>
  getJson(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const fetchMyThreads = () => getJson('/api/contact_teacher/mine');

export const fetchTeacherUnread = () => getJson('/api/contact_teacher/unread');

export const sendThreadReply = (threadId, text) =>
  postJson(`/api/contact_teacher/${encodeURIComponent(threadId)}/reply`, { text });

export const markThreadRead = (threadId) =>
  postJson(`/api/contact_teacher/${encodeURIComponent(threadId)}/read`, {});

export const CHAT_TIMEOUT_MS = 150000;
export const HISTORY_TIMEOUT_MS = 30000;
